import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Clock, MapPin, Loader2, AlertTriangle, Image, ChevronLeft, ChevronRight, X, Edit2, Save, Copy, Check, FileText, PenTool, Trash2 } from 'lucide-react';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UpdateDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [update, setUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [generatingScope, setGeneratingScope] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    job_name: '',
    site_address: '',
    update_text: '',
    category: '',
    urgency: '',
    required_by: ''
  });

  useEffect(() => {
    fetchUpdate();
  }, [id]);

  const fetchUpdate = async () => {
    try {
      const response = await axios.get(`${API_URL}/updates/${id}`);
      setUpdate(response.data);
      setEditForm({
        job_name: response.data.job_name || '',
        site_address: response.data.site_address || '',
        update_text: response.data.update_text || '',
        category: response.data.category || '',
        urgency: response.data.urgency || '',
        required_by: response.data.required_by ? response.data.required_by.slice(0, 16) : ''
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load update');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.update_text.trim()) {
      toast.error('Update text is required');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`${API_URL}/updates/${id}`, editForm);
      toast.success('Update saved!');
      setIsEditing(false);
      fetchUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/updates/${id}`);
      toast.success('Deleted successfully');
      navigate('/tradie/deliver');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      job_name: update.job_name || '',
      site_address: update.site_address || '',
      update_text: update.update_text || '',
      category: update.category || '',
      urgency: update.urgency || '',
      required_by: update.required_by ? update.required_by.slice(0, 16) : ''
    });
    setIsEditing(false);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const nextPhoto = () => {
    setLightboxIndex((prev) => (prev + 1) % update.photos.length);
  };

  const prevPhoto = () => {
    setLightboxIndex((prev) => (prev - 1 + update.photos.length) % update.photos.length);
  };

  const handleCopyText = async () => {
    if (!update?.update_text) return;
    
    const textArea = document.createElement('textarea');
    textArea.value = update.update_text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, update.update_text.length);
    
    let success = false;
    
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }
    
    if (!success && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(update.update_text);
        success = true;
      } catch (err) {
        console.error('Clipboard API failed:', err);
      }
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Try selecting the text manually.');
    }
  };

  const handleGenerateScope = async () => {
    if (!update?.update_text) return;
    
    setGeneratingScope(true);
    
    try {
      const webhookUrl = 'https://etradinggroup.app.n8n.cloud/webhook/draft-scope-emergent';
      
      // Extract the update text - it may contain structured data from the original form
      const payload = {
        job_notes: update.update_text,
        approximate_area: '',
        key_materials_system: '',
        specific_requests: '',
        access_site_conditions: ''
      };

      const response = await axios.post(webhookUrl, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data.status === 'success') {
        toast.success('Draft scope generated!');
        // Store in sessionStorage and navigate to result page
        sessionStorage.setItem('draftScopeResult', JSON.stringify(response.data));
        navigate('/tradie/draft-scope-result');
      } else {
        toast.error('Failed to generate scope');
      }
      
    } catch (error) {
      console.error('Scope generation error:', error);
      toast.error('Failed to connect to scope generator. Please try again.');
    } finally {
      setGeneratingScope(false);
    }
  };

  // Check if this is a "Scope Notes" entry that can be converted
  const canGenerateScope = update?.job_name === 'Scope Notes' || 
                           (update?.category === 'Win Work' && update?.job_name !== 'Draft Scope of Works');

  const getStatusClass = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Done': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getCategoryClass = (category) => {
    switch (category) {
      case 'Win Work': return 'bg-purple-100 text-purple-700';
      case 'Deliver Work': return 'bg-teal-100 text-teal-700';
      case 'Get Paid': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-500';
    }
  };

  const getUrgencyClass = (urgency) => {
    switch (urgency) {
      case 'Low': return 'bg-slate-100 text-slate-600';
      case 'Medium': return 'bg-orange-100 text-orange-700';
      case 'High': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <Button onClick={() => navigate('/tradie/log-update', { state: { scrollToTasks: true } })}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => navigate('/tradie/log-update', { state: { scrollToTasks: true } })}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold tracking-tight truncate">
              {isEditing ? 'Edit Update' : (update.job_name || 'Update Details')}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage="deliver" />
            {!isEditing ? (
              /* Hide Edit button for Signed SWMS - it's a finished record */
              <div className="flex gap-2">
                {update.job_name !== 'Signed SWMS' && (
                  <Button
                    variant="ghost"
                    data-testid="edit-btn"
                    onClick={() => setIsEditing(true)}
                    className="text-orange-400 hover:text-orange-300 hover:bg-slate-700"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
                <Button
                  variant="ghost"
                  data-testid="delete-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  className="text-red-400 hover:text-red-300 hover:bg-slate-700"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  data-testid="cancel-edit-btn"
                  onClick={handleCancel}
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-edit-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="deliver" activeModule="update" />

      {/* Content */}
      <main className="p-4 max-w-2xl mx-auto">
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader className="pb-3">
            {!isEditing ? (
              <>
                <CardTitle className="font-heading text-xl">
                  {update.job_name || <span className="text-slate-400 italic">No job name</span>}
                </CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusClass(update.status)}`}>
                    {update.status}
                  </span>
                  {update.category && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryClass(update.category)}`}>
                      {update.category}
                    </span>
                  )}
                  {update.urgency && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getUrgencyClass(update.urgency)}`}>
                      {update.urgency}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <CardTitle className="font-heading text-lg text-orange-600">Edit Your Update</CardTitle>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              /* Edit Mode */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Job Name</Label>
                  <Input
                    data-testid="edit-job-name"
                    value={editForm.job_name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, job_name: e.target.value }))}
                    placeholder="e.g. Smith Renovation"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">Address or Contact</Label>
                  <Input
                    data-testid="edit-site-address"
                    value={editForm.site_address}
                    onChange={(e) => setEditForm(prev => ({ ...prev, site_address: e.target.value }))}
                    placeholder="e.g. 123 Main St or John 0412 345 678"
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-semibold">Update <span className="text-red-500">*</span></Label>
                  <Textarea
                    data-testid="edit-update-text"
                    value={editForm.update_text}
                    onChange={(e) => setEditForm(prev => ({ ...prev, update_text: e.target.value }))}
                    placeholder="Describe the update..."
                    className="min-h-[120px]"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-semibold">Category</Label>
                    <Select 
                      value={editForm.category} 
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger data-testid="edit-category" className="h-12">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Win Work">Win Work</SelectItem>
                        <SelectItem value="Deliver Work">Deliver Work</SelectItem>
                        <SelectItem value="Get Paid">Get Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-semibold">Urgency</Label>
                    <Select 
                      value={editForm.urgency} 
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, urgency: v }))}
                    >
                      <SelectTrigger data-testid="edit-urgency" className="h-12">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Required By */}
                <div className="space-y-2">
                  <Label className="font-semibold">Required By</Label>
                  <Input
                    data-testid="edit-required-by"
                    type="datetime-local"
                    value={editForm.required_by}
                    onChange={(e) => setEditForm(prev => ({ ...prev, required_by: e.target.value }))}
                    className="h-12"
                  />
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                {/* Date/Time */}
                <div className="flex items-center gap-2 text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">
                    {format(new Date(update.created_at), 'EEEE, MMMM d, yyyy - h:mm a')}
                  </span>
                </div>

                {/* Address or Contact */}
                {update.site_address && (
                  <div className="flex items-start gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 mt-0.5" />
                    <span>{update.site_address}</span>
                  </div>
                )}

                {/* Required By */}
                {update.required_by && (
                  <div className="flex items-center gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">
                      Required by: {format(new Date(update.required_by), 'MMM d, yyyy - h:mm a')}
                    </span>
                  </div>
                )}

                {/* Photos */}
                {update.photos && update.photos.length > 0 && (
                  <>
                    {/* For Signed SWMS, skip the first photo (signature) in the photos section */}
                    {update.job_name === 'Signed SWMS' ? (
                      /* Only show non-signature photos if there are more than 1 */
                      update.photos.length > 1 && (
                        <div className="pt-4 border-t border-slate-200">
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Image className="w-4 h-4" />
                            Photos ({update.photos.length - 1})
                          </h4>
                          <div className="grid grid-cols-3 gap-2">
                            {update.photos.slice(1).map((photo, index) => (
                              <button
                                key={index}
                                onClick={() => openLightbox(index + 1)}
                                className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 hover:border-orange-400 transition-colors"
                                data-testid={`photo-thumbnail-${index}`}
                              >
                                <img
                                  src={photo}
                                  alt={`Photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    ) : (
                      /* Regular photos display */
                      <div className="pt-4 border-t border-slate-200">
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Image className="w-4 h-4" />
                          Photos ({update.photos.length})
                        </h4>
                        <div className="grid grid-cols-3 gap-2">
                          {update.photos.map((photo, index) => (
                            <button
                              key={index}
                              onClick={() => openLightbox(index)}
                              className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 hover:border-orange-400 transition-colors"
                              data-testid={`photo-thumbnail-${index}`}
                            >
                              <img
                                src={photo}
                                alt={`Photo ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Update Text */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-700">Update Details</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="copy-update-btn"
                      onClick={handleCopyText}
                      className={copied ? "bg-green-100 border-green-300" : ""}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-1 text-green-600" />
                          <span className="text-green-600">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-slate-600 whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                    {update.update_text}
                  </p>
                  
                  {/* Signature Image - shown at bottom for Signed SWMS */}
                  {update.job_name === 'Signed SWMS' && update.photos && update.photos.length > 0 && (
                    <div className="mt-4 border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50">
                      <div className="flex items-center gap-2 mb-3">
                        <PenTool className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-semibold text-emerald-700">Signature</span>
                      </div>
                      <img
                        src={update.photos[0]}
                        alt="Signature"
                        className="w-full max-h-[120px] object-contain bg-white rounded border border-emerald-100"
                        data-testid="signature-image"
                      />
                    </div>
                  )}
                </div>

                {/* Admin Notes (if any) */}
                {update.admin_notes && (
                  <div className="pt-4 border-t border-slate-200 bg-blue-50 -mx-6 px-6 py-4 rounded-b-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">📝 Admin Notes</h4>
                    <p className="text-blue-700 whitespace-pre-wrap">{update.admin_notes}</p>
                  </div>
                )}

                {/* Follow-up Date (if set) */}
                {update.follow_up_date && (
                  <div className="flex items-center gap-2 text-orange-600 bg-orange-50 p-3 rounded-lg">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">
                      Follow-up: {format(new Date(update.follow_up_date), 'MMM d, yyyy - h:mm a')}
                    </span>
                  </div>
                )}

                {/* Generate Scope Button - for Scope Notes entries */}
                {canGenerateScope && (
                  <div className="pt-4 border-t border-slate-200">
                    <Button
                      data-testid="generate-scope-from-update-btn"
                      onClick={handleGenerateScope}
                      disabled={generatingScope}
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {generatingScope ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Scope...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Draft Scope
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-slate-500 text-center mt-2">
                      Convert these notes into a professional scope of works
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Lightbox Modal */}
      {lightboxOpen && update.photos && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/20 rounded-full"
            onClick={closeLightbox}
            data-testid="close-lightbox-btn"
          >
            <X className="w-6 h-6" />
          </button>
          
          {update.photos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/20 rounded-full"
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                data-testid="prev-photo-btn"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/20 rounded-full"
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                data-testid="next-photo-btn"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          
          <img
            src={update.photos[lightboxIndex]}
            alt={`Photo ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {lightboxIndex + 1} / {update.photos.length}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Delete Update?</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this update? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <FooterNav />
    </div>
  );
};

export default UpdateDetail;
